import { forwardRef, useMemo, ComponentPropsWithoutRef } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface DateRangeTriggerProps extends ComponentPropsWithoutRef<typeof Button> {
  dateRange?: DateRange;
}

export const DateRangeTrigger = forwardRef<HTMLButtonElement, DateRangeTriggerProps>(
  ({ dateRange, className, ...props }, ref) => {
    const { t } = useTranslation();

    const displayValue = useMemo(() => {
      if (!dateRange?.from) {
        return t('welcome.selectdates.label');
      }

      if (dateRange.to) {
        return `${format(dateRange.from, 'MMM dd, yyyy')} - ${format(dateRange.to, 'MMM dd, yyyy')}`;
      }

      return format(dateRange.from, 'MMM dd, yyyy');
    }, [dateRange, t]);

    return (
      <Button
        ref={ref}
        variant="outline"
        className={cn(
          'w-[260px] justify-start text-left font-normal',
          !dateRange && 'text-muted-foreground',
          className
        )}
        {...props}>
        <CalendarIcon className="mr-2 h-4 w-4" />
        {displayValue}
      </Button>
    );
  }
);

DateRangeTrigger.displayName = 'DateRangeTrigger';
